import React, { useEffect, useState, useCallback } from "react"
import API from "../../api/axios"
import AdminLayout from "./AdminLayout"

const AdminPayments = () => {
  const [payments, setPayments]   = useState([])
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState("")
  const [success, setSuccess]     = useState("")
  const [statusFilter, setFilter] = useState("")
  const [page, setPage]           = useState(1)
  const [pagination, setPagination] = useState({})
  const [approving, setApproving] = useState(null)

  const fetchPayments = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page, limit: 20 })
      if (statusFilter) params.append("status", statusFilter)
      const { data } = await API.get(`/payments?${params}`)
      setPayments(data.data)
      setPagination(data.pagination || {})
    } catch { setError("Failed to load payments.") }
    finally { setLoading(false) }
  }, [page, statusFilter])

  useEffect(() => { fetchPayments() }, [fetchPayments])

  const approve = async (id) => {
    setApproving(id); setError(""); setSuccess("")
    try {
      await API.patch(`/payments/${id}/approve`)
      setSuccess("Payment approved and student enrolled.")
      fetchPayments()
    } catch (err) {
      setError(err.response?.data?.message || "Approval failed.")
    } finally { setApproving(null) }
  }

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"

  return (
    <AdminLayout title="Payments">
      {error   && <div className="admin-alert admin-alert-error">{error}</div>}
      {success && <div className="admin-alert admin-alert-success">{success}</div>}

      <div className="admin-section">
        <div className="admin-section-header">
          <h3>All Payments ({pagination.total || 0})</h3>
          <div className="admin-filters">
            <select className="admin-select" value={statusFilter} onChange={e => { setFilter(e.target.value); setPage(1) }}>
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Course</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Status</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr className="admin-loading-row">
                  <td colSpan="7"><i className="fa fa-spinner fa-spin"></i> Loading…</td>
                </tr>
              )}
              {!loading && payments.length === 0 && (
                <tr><td colSpan="7" className="admin-empty">No payments found.</td></tr>
              )}
              {!loading && payments.map(p => (
                <tr key={p.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{p.user?.name}</div>
                    <small style={{ color: "#999" }}>{p.user?.email}</small>
                    {p.user?.phone && <div style={{ fontSize: 11, color: "#bbb" }}>{p.user.phone}</div>}
                  </td>
                  <td style={{ maxWidth: 160 }}>{p.course?.title}</td>
                  <td style={{ fontWeight: 700 }}>KES {Number(p.amount).toLocaleString()}</td>
                  <td>
                    <span className="badge badge-read" style={{ textTransform: "uppercase" }}>{p.method}</span>
                  </td>
                  <td><span className={`badge badge-${p.status}`}>{p.status}</span></td>
                  <td style={{ fontSize: 12, color: "#888" }}>{fmtDate(p.createdAt)}</td>
                  <td>
                    {p.status === "pending" ? (
                      <button
                        className="btn-sm btn-approve"
                        onClick={() => approve(p.id)}
                        disabled={approving === p.id}
                      >
                        {approving === p.id ? <i className="fa fa-spinner fa-spin"></i> : "✓ Approve"}
                      </button>
                    ) : (
                      <span style={{ fontSize: 12, color: "#aaa" }}>
                        {p.status === "completed" ? (p.approvedAt ? `Approved ${fmtDate(p.approvedAt)}` : "Auto-approved") : "—"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination.pages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 8, padding: "16px" }}>
            <button className="btn-sm btn-toggle" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span style={{ padding: "5px 12px", fontSize: 13, color: "#666" }}>Page {page} of {pagination.pages}</span>
            <button className="btn-sm btn-toggle" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminPayments
